/**
 * AdminResultsTab Component
 * Test results tab for admin panel
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Calendar, Trophy, Clock } from 'lucide-react';

const AdminResultsTab = ({
  attempts,
  users,
  categories,
  attemptFilterUser,
  setAttemptFilterUser,
  attemptFilterTrack,
  setAttemptFilterTrack,
  formatDate,
  formatTime
}) => {
  const filteredAttempts = (attempts || []).filter(a => {
    const matchesUser = !attemptFilterUser || a.user_id === attemptFilterUser;
    const matchesTrack = !attemptFilterTrack || a.exam_type === attemptFilterTrack;
    return matchesUser && matchesTrack;
  });

  return (
    <Card className="bg-white dark:bg-slate-900">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle>Test Results</CardTitle>
          <div className="flex gap-2">
            <Select value={attemptFilterUser || "all"} onValueChange={(v) => setAttemptFilterUser(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="filter-user">
                <SelectValue placeholder="All Participants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Participants</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={attemptFilterTrack || "all"} onValueChange={(v) => setAttemptFilterTrack(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32" data-testid="filter-track">
                <SelectValue placeholder="All Tracks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tracks</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Track</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-center">Questions</TableHead>
                <TableHead className="text-center">Correct</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="text-center">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAttempts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                    No results found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAttempts.map((a) => (
                  <TableRow key={a.attempt_id} data-testid={`attempt-row-${a.attempt_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{a.user_name}</p>
                        <p className="text-xs text-slate-500">{a.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={a.exam_type === 'PMP' ? 'border-blue-500 text-blue-600' : 'border-green-500 text-green-600'}
                      >
                        {a.exam_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.mode === 'exam' ? 'default' : 'secondary'}>
                        {a.mode === 'exam' ? 'Exam' : 'Training'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400 text-sm">
                        <Calendar className="w-3 h-3" />
                        {formatDate(a.completed_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(a.correct_count || 0) + (a.incorrect_count || 0)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-green-600 font-medium">{a.correct_count || 0}</span>
                      <span className="text-slate-400"> / </span>
                      <span className="text-red-500">{a.incorrect_count || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Trophy className={`w-4 h-4 ${
                          a.score >= 70 ? 'text-green-500' : 
                          a.score >= 50 ? 'text-yellow-500' : 'text-red-500'
                        }`} />
                        <span className={`font-bold ${
                          a.score >= 70 ? 'text-green-600' : 
                          a.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {a.score?.toFixed(1)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 text-slate-600">
                        <Clock className="w-3 h-3" />
                        {formatTime(a.time_used)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
        <div className="mt-4 text-sm text-slate-500">
          {filteredAttempts.length} results found
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminResultsTab;
